import { ArrowLeft, ChevronLeft, ChevronRight, Trophy, User, FileText } from 'lucide-react'
import { useChallengeHistory } from '@/hooks/useStrategies'
import { cn, formatOdds } from '@/lib/utils'
import type { ChallengeHistoryItem, ChallengeSummary } from '@/types/strategies'

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  ganada: { label: 'Acertó', className: 'bg-success/15 text-success border-success/40' },
  perdida: { label: 'Falló', className: 'bg-danger/15 text-danger border-danger/40' },
  void: { label: 'Anulado', className: 'bg-panel-elevated text-text-dim border-border/50' },
  pendiente: { label: 'Pendiente', className: 'bg-brand/15 text-brand border-brand/40' },
}

function rangeLabel(challenge: ChallengeSummary): string {
  if (!challenge.week_start_date || !challenge.week_end_date) return challenge.name
  const s = new Date(`${challenge.week_start_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  const e = new Date(`${challenge.week_end_date}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
  return `${s} - ${e}`
}

function WeekSparkline({ items, tone }: { items: ChallengeHistoryItem[]; tone: 'yellow' | 'brand' }) {
  const points = items
    .filter((it) => it.bankroll_after != null)
    .sort((a, b) => (a.step_date ?? '').localeCompare(b.step_date ?? ''))
    .map((it) => it.bankroll_after as number)
  if (points.length < 2) return null
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const w = 100
  const h = 36
  const coords = points.map((v, i) => {
    const x = (i / (points.length - 1)) * w
    const y = h - ((v - min) / range) * h
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none">
      <polyline points={coords.join(' ')} fill="none" className={tone === 'yellow' ? 'stroke-yellow-400' : 'stroke-brand'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** P1.9F (25/09/2026, referencia visual aprobada -- "Reto - Semana"): resumen semanal +
 * "Evolución diaria" en tabla (Sección 3 del encargo: día/stake/cuota/resultado/banca) --
 * mismo `useChallengeHistory` ya existente, reutilizado sin cambios. Navegación `< >` entre
 * semanas cronológicamente adyacentes de la MISMA serie (Oficial o Mi Reto), nunca mezcla
 * ambas series. */
export function WeekView({
  challenge, isOfficial, siblings, onBack, onSelectChallenge, onOpenPick,
}: {
  challenge: ChallengeSummary
  isOfficial: boolean
  /** Todas las semanas de la MISMA serie (oficial o personal), ordenadas por fecha --
   * permite `< >` sin volver a pedir la lista completa. */
  siblings: ChallengeSummary[]
  onBack: () => void
  onSelectChallenge: (challengeId: number) => void
  onOpenPick: (item: ChallengeHistoryItem) => void
}) {
  const { data: history, isLoading } = useChallengeHistory(challenge.challenge_id)
  const items = history?.items ?? []
  const picksCount = items.filter((it) => it.display_market != null).length
  const totalCount = items.length
  const resultAmount = challenge.current_bankroll - challenge.bankroll_initial
  const positive = resultAmount >= 0
  const toneClass = isOfficial ? 'text-yellow-400' : 'text-brand'

  const idx = siblings.findIndex((c) => c.challenge_id === challenge.challenge_id)
  const prev = idx >= 0 ? siblings[idx + 1] : null // siblings ordenados desc por fecha -- "anterior" = índice mayor
  const next = idx >= 0 ? siblings[idx - 1] : null

  const sortedItems = [...items].sort((a, b) => (a.step_date ?? '').localeCompare(b.step_date ?? ''))

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-xl bg-panel-elevated hover:bg-border text-text transition">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-lg font-black text-text">Reto - Semana</h1>
          <p className="text-[10px] text-text-dim uppercase tracking-wider font-bold">John Anthony</p>
        </div>
      </div>

      <div className="flex items-center justify-between bg-panel-elevated/40 border border-border/30 rounded-xl px-2 py-2">
        <button
          disabled={!prev}
          onClick={() => prev && onSelectChallenge(prev.challenge_id)}
          className="p-1.5 rounded-lg text-text-dim disabled:opacity-30 disabled:pointer-events-none hover:text-text"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs font-black text-text">{rangeLabel(challenge)}</span>
        <button
          disabled={!next}
          onClick={() => next && onSelectChallenge(next.challenge_id)}
          className="p-1.5 rounded-lg text-text-dim disabled:opacity-30 disabled:pointer-events-none hover:text-text"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className={cn('card p-4 space-y-3 border', isOfficial ? 'border-yellow-500/40' : 'border-brand/30')}>
        <div className="flex items-center gap-2">
          {isOfficial ? <Trophy className="h-4 w-4 text-yellow-400" /> : <User className="h-4 w-4 text-brand" />}
          <div>
            <p className="text-sm font-black text-text">{challenge.status === 'ACTIVE' ? 'Semana en curso' : 'Semana finalizada'}</p>
            <p className="text-[10px] text-text-dim">
              {totalCount > 0 ? `Seguimiento completo de tus ${totalCount} oportunidades.` : 'Todavía sin oportunidades esta semana.'}
            </p>
          </div>
          <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full bg-panel text-text-dim border border-border/50 shrink-0">
            {challenge.status === 'ACTIVE' ? 'Activo' : challenge.status === 'COMPLETED' ? 'Completado' : challenge.status === 'FAILED' ? 'Fallido' : challenge.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Banca inicial</p>
            <p className="figure text-sm font-bold text-text">{challenge.bankroll_initial.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Banca actual</p>
            <p className={cn('figure text-sm font-bold', toneClass)}>{challenge.current_bankroll.toFixed(2)}€</p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Resultado</p>
            <p className={cn('figure text-sm font-bold', positive ? 'text-success' : 'text-danger')}>
              {positive ? '+' : ''}{resultAmount.toFixed(2)}€
            </p>
          </div>
          <div className="bg-panel-elevated/40 p-2.5 rounded-xl border border-border/30">
            <p className="text-[9px] font-bold uppercase text-text-dim">Picks</p>
            <p className="figure text-sm font-bold text-text">{picksCount} / {totalCount || '—'}</p>
          </div>
        </div>

        {items.length >= 2 && <WeekSparkline items={items} tone={isOfficial ? 'yellow' : 'brand'} />}
      </div>

      <div className="card p-4 space-y-2">
        <p className="eyebrow">Evolución diaria</p>
        {isLoading && <p className="text-xs text-text-dim">Cargando…</p>}
        {!isLoading && sortedItems.length === 0 && (
          <p className="text-xs text-text-dim italic">Todavía no hay pasos registrados esta semana.</p>
        )}
        {sortedItems.length > 0 && (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-[10px] min-w-[420px]">
              <thead>
                <tr className="text-text-dim uppercase text-[8px]">
                  <th className="text-left font-bold pb-1.5 px-1">Día</th>
                  <th className="text-right font-bold pb-1.5 px-1">Stake</th>
                  <th className="text-right font-bold pb-1.5 px-1">Cuota</th>
                  <th className="text-center font-bold pb-1.5 px-1">Resultado</th>
                  <th className="text-right font-bold pb-1.5 px-1">Banca</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((it) => {
                  const badge = STATUS_BADGE[it.status] ?? STATUS_BADGE.pendiente
                  const isNoBet = it.display_market == null
                  return (
                    <tr
                      key={it.step_id}
                      onClick={() => !isNoBet && onOpenPick(it)}
                      className={cn('border-t border-border/20', !isNoBet && 'cursor-pointer hover:bg-panel-elevated/30 active-scale')}
                    >
                      <td className="py-1.5 px-1 font-bold text-text">
                        {it.step_date ? new Date(`${it.step_date}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short' }) : '—'}
                      </td>
                      <td className="py-1.5 px-1 text-right figure text-text">{isNoBet ? '—' : `${it.stake.toFixed(2)}€`}</td>
                      <td className="py-1.5 px-1 text-right figure text-text">{isNoBet ? '—' : it.odds != null ? formatOdds(it.odds) : '—'}</td>
                      <td className="py-1.5 px-1 text-center">
                        <span className={cn('inline-block px-1.5 py-0.5 rounded-full border text-[8px] font-black uppercase', badge.className)}>
                          {isNoBet ? 'Sin pick' : badge.label}
                        </span>
                      </td>
                      <td className="py-1.5 px-1 text-right figure text-text">{it.bankroll_after != null ? `${it.bankroll_after.toFixed(2)}€` : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <button className="w-full flex items-center justify-center gap-1.5 py-3 rounded-xl bg-panel-elevated border border-border/60 text-text text-xs font-black uppercase tracking-wider">
        <FileText className="h-3.5 w-3.5" /> Ver resumen e insights de la semana
      </button>
    </div>
  )
}
